import React, {useState, useMemo, useCallback, useEffect, useRef} from 'react'
import {useNavigate} from 'react-router-dom'
import {Select, Spin, Input, Alert} from 'antd'
import {NotificationOutlined, TeamOutlined, CheckCircleOutlined} from '@ant-design/icons'
import {useMutation} from '@tanstack/react-query'
import {useAPI} from '../../config/API'
import SystemNotification from '../../models/SystemNotification'
import SpinLoader from '../../components/SpinLoader'
import Form, {FormInputComponent} from '../../components/form/Form'
import User from "../../models/User";
import UserImageCircle from "../../components/UserImageCircle";
import TalentLocationRadioSelect from '../../components/TalentLocationRadioSelect';
import {TalentMessageDetailView} from "../talent-messages/TalentMessageDetail";

const defaultParams = {
    statuses: ['Active'],
    per_page: 50
}
// ─── Disciplines from Laravel injection ──────────────────────────────────────

const disciplineCategories = TB.map([...laravel.discipline_categories], cat => {
    let option = TB.pick(cat, ['id', 'name', 'disciplines', 'display_order'])
    let disciplines = option.disciplines;
    option.disciplines = TB.map(disciplines, discipline => TB.pick(discipline, ['id', 'name', 'display_order']))
    option.value = option.id;
    return option
});

// Flat discipline list — used for name lookups in audience labels
const allDisciplines = SystemNotification.allDisciplines();

const disciplineOptions = disciplineCategories.map(cat => ({
    label: cat.name,
    options: cat.disciplines.map(d => ({label: d.name, value: d.id})),
}))

// Map discipline id → category name for filterOption category matching
const disciplineCategoryMap = {}
disciplineCategories.forEach(cat => {
    cat.disciplines.forEach(d => { disciplineCategoryMap[d.id] = cat.name })
})

// ─── Static config ────────────────────────────────────────────────────────────

const DEFAULT_SENDERS = [
    {name: 'Wripple Client Team', email: 'ClientTeam@wripple.com'},
    {name: 'Wripple Talent Team', email: 'TalentTeam@wripple.com'},
]

const AUDIENCE_TYPES = [
    {
        key: 'all',
        label: 'All Talent',
        desc: 'Every active talent on the platform',
        color: 'bg-indigo-50 stroke-indigo-600',
        icon: 'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75'
    },
    {
        key: 'discipline',
        label: 'By Discipline',
        desc: 'Target by role or craft',
        color: 'bg-violet-100 stroke-violet-700',
        icon: 'M12 4a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM6 20v-2a6 6 0 0 1 12 0v2'
    },
    {
        key: 'geo',
        label: 'By Geography',
        desc: 'Target by Region or Proximity to Location',
        color: 'bg-teal-100 stroke-teal-700',
        icon: 'M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0zM12 10a3 3 0 1 0 0-6 3 3 0 0 0 0 6z'
    },
    {
        key: 'specific',
        label: 'Specific Talent',
        desc: 'Search and hand-pick individual people from the talent roster',
        color: 'bg-green-50 stroke-green-700',
        icon: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 3a4 4 0 1 0 0 8 4 4 0 0 0 0-8zM16 11l1.5 1.5L21 9',
        fullWidth: true
    },
]

const MESSAGE_CATEGORIES = [
    {
        value: 1,
        label: 'Announcement',
        description: 'Send a general message to all talent, or filter by location or discipline.',
        icon: NotificationOutlined,
        colorKey: 'color.secondary',
    },
    {
        value: 2,
        label: 'Potential Talent Matches for Role',
        description: "Reach talent whose skills and availability fit an active Job Board role.",
        icon: TeamOutlined,
        colorKey: 'color.warning-dark',
    },
    {
        value: 3,
        label: 'Shortlisted Candidates for Role',
        description: 'Message candidates already shortlisted for a role. Available only when a role has shortlisted talent.',
        icon: CheckCircleOutlined,
        colorKey: 'color.info-dark',
    },
]

const {CLIENT_CATEGORIES} = SystemNotification

const ANNOUNCEMENT_ACTIONS = [
    {label: 'No Action (Default)', value: 1},
    {label: 'Edit Availability', value: 2},
    {label: 'Edit Profile', value: 3},
    {label: 'View Opportunities', value: 4},
    {label: 'View Job Board', value: 5},
]
const AVATAR_COLORS = [
    '#4F54E8', '#0F766E', '#6D28D9', '#065F46',
    '#92400E', '#1D4ED8', '#0369A1', '#7C3AED', '#B45309', '#991B1B'
]

// ─── Utilities ───────────────────────────────────────────────────────────────

const mapUser = (u) => (User.make({
    id: u.id,
    name: u.name || [u.first_name, u.last_name].filter(Boolean).join(' '),
    disc: u.talent_disciplines?.[0]?.name || '',
    loc: [u.location?.city, u.location?.state].filter(Boolean).join(', ') || u.location?.country || '',
    status: u.display_status || u.status || 'Active',
    ...u
}))

const cx = (...classes) => classes.filter(Boolean).join(' ')

// ─── Shared class strings ─────────────────────────────────────────────────────

const btnPrimary = 'px-5 py-2.5 bg-indigo-600 text-white text-[13.5px] font-semibold cursor-pointer hover:bg-indigo-700 transition-colors flex items-center gap-1.5'
const btnSecondary = 'px-5 py-2.5 bg-white text-gray-500 border border-gray-200 text-[13.5px] font-medium cursor-pointer hover:bg-gray-50 hover:text-gray-800 transition-colors'
const inputBase = 'w-full py-2.5 px-3 border border-gray-200 text-sm text-gray-900 bg-white outline-none focus:border-indigo-600 transition-colors'
const selectBase = cx('am-select', inputBase, 'cursor-pointer')
const labelBase = 'block text-xs font-semibold text-gray-500 tracking-wide mb-1.5'
const chipBase = 'py-1 px-2.5 border-[1.5px] text-xs font-medium cursor-pointer transition-colors'
const chipActive = 'border-indigo-600 bg-indigo-50 text-indigo-600'
const chipInactive = 'border-gray-200 text-gray-500 bg-white hover:border-gray-300'

// ─── Small atoms ─────────────────────────────────────────────────────────────


const Checkbox = ({checked}) => (
    <div
        className={cx('w-4 h-4 border-[1.5px] shrink-0 flex items-center justify-center transition-colors', checked ? 'bg-indigo-600 border-indigo-600' : 'border-gray-200 bg-white')}>
        {checked && (
            <svg width="10" height="10" fill="none" stroke="white" strokeWidth="2.5" viewBox="0 0 24 24">
                <polyline points="20,6 9,17 4,12"/>
            </svg>
        )}
    </div>
)

// ─── Steps Bar ───────────────────────────────────────────────────────────────

const StepsBar = ({step}) => {
    const steps = ['Message Basics', 'Choose Audience', 'Configure Targeting', 'Review & Send']
    return (
        <div className="flex items-center mb-7">
            {steps.map((label, i) => {
                const n = i + 1
                const done = n < step
                const active = n === step
                return (
                    <div key={n} className="flex items-center gap-2 flex-1">
                        <div className={cx(
                            'w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold border-2 shrink-0 transition-all',
                            done ? 'bg-emerald-500 border-emerald-500 text-white' :
                                active ? 'bg-indigo-600 border-indigo-600 text-white' :
                                    'border-gray-200 text-gray-400 bg-white'
                        )}>
                            {done ? '✓' : n}
                        </div>
                        <span className={cx(
                            'text-xs whitespace-nowrap',
                            done ? 'font-medium text-gray-500' :
                                active ? 'font-semibold text-indigo-600' :
                                    'font-medium text-gray-400'
                        )}>
                            {label}
                        </span>
                        {i < steps.length - 1 && <div className="flex-1 h-px bg-gray-200 mx-2.5"/>}
                    </div>
                )
            })}
        </div>
    )
}

// ─── Talent Picker List ───────────────────────────────────────────────────────

const TalentPickerList = ({
    selected,
    selectedObjects,
    onToggle,
    searchPlaceholder = 'Search by name…'
}) => {
    const [search, setSearch] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')

    useEffect(() => {
        if (!search) { setDebouncedSearch(''); return }
        const t = setTimeout(() => setDebouncedSearch(search), 350)
        return () => clearTimeout(t)
    }, [search])

    const {data: searchData, isLoading: searchLoading} = useAPI(
        ['searchTalentForMessaging', {name: debouncedSearch, ...defaultParams}],
        {enabled: debouncedSearch.length >= 2}
    )

    const results = useMemo(() => {
        if (!searchData?.users) return []
        return searchData.users.map(mapUser)
    }, [searchData])

    const selectedList = useMemo(
        () => [...selected].map(id => selectedObjects[id]).filter(Boolean),
        [selected, selectedObjects]
    )

    return (
        <div className="flex flex-col gap-2">
            {selectedList.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {selectedList.map(t => (
                        <span
                            key={t.id}
                            onClick={() => onToggle(t)}
                            className="inline-flex items-center gap-1 py-0.5 px-2 bg-indigo-50 border border-indigo-300 text-xs font-medium text-indigo-600 cursor-pointer hover:bg-indigo-100"
                        >
                            {t.name}
                            <span className="opacity-50">×</span>
                        </span>
                    ))}
                </div>
            )}
            <input
                className="am-search w-full py-2 px-3 pl-8 border border-gray-200 text-[13.5px] text-gray-900 bg-white outline-none focus:border-indigo-600 transition-colors"
                placeholder={searchPlaceholder}
                value={search}
                onChange={e => setSearch(e.target.value)}
            />
            <div className="border border-gray-200 overflow-y-auto bg-white am-scroll" style={{maxHeight: '280px'}}>
                {debouncedSearch.length < 2 ? (
                    selectedList.length === 0
                        ? <div className="py-7 text-center text-gray-400 text-[13px]">Search for talent by name to add them</div>
                        : selectedList.map(t => (
                            <div
                                key={t.id}
                                onClick={() => onToggle(t)}
                                className="flex items-center gap-3 py-2.5 px-3.5 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-indigo-50/60 transition-colors bg-indigo-50"
                            >
                                <Checkbox checked={true}/>
                                <UserImageCircle user={t} size={32}/>
                                <div>
                                    <div className="text-[13px] font-medium">{t.name}</div>
                                    <div className="text-[11.5px] text-gray-400">
                                        {t.disc}{t.loc ? ` · ${t.loc}` : ''}
                                    </div>
                                </div>
                            </div>
                        ))
                ) : searchLoading ? (
                    <div className="py-7 text-center text-gray-400 text-[13px]">Searching…</div>
                ) : results.length === 0 ? (
                    <div className="py-7 text-center text-gray-400 text-[13px]">No talent matched your search</div>
                ) : results.map(t => {
                    const sel = selected.has(t.id)
                    return (
                        <div
                            key={t.id}
                            onClick={() => onToggle(t)}
                            className={cx('flex items-center gap-3 py-2.5 px-3.5 border-b border-gray-100 last:border-b-0 cursor-pointer hover:bg-gray-50 transition-colors', sel && 'bg-indigo-50')}
                        >
                            <Checkbox checked={sel}/>
                            <UserImageCircle user={t} size={32}/>
                            <div>
                                <div className="text-[13px] font-medium">{t.name}</div>
                                <div className="text-[11.5px] text-gray-400">
                                    {t.disc}{t.loc ? ` · ${t.loc}` : ''}
                                </div>
                            </div>
                        </div>
                    )
                })}
            </div>
            <div className="flex justify-between items-center">
                <span className="text-xs text-gray-400">
                    {selected.size > 0 ? `${selected.size} selected` : 'No talent selected'}
                </span>
            </div>
        </div>
    )
}

// ─── Add Talent Panel ─────────────────────────────────────────────────────────
// Self-contained panel with its own debounced name search against the API.
// Lives below the RecipientList and is visually connected via border-t-0.

const AddTalentPanel = ({matches, customAdded, onToggleCustomAdd}) => {
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')
    const [debouncedQuery, setDebouncedQuery] = useState('')

    useEffect(() => {
        if (!query) { setDebouncedQuery(''); return }
        const t = setTimeout(() => setDebouncedQuery(query), 350)
        return () => clearTimeout(t)
    }, [query])

    const searchParams = useMemo(() => ({name: debouncedQuery}), [debouncedQuery])

    const {data: searchData, isLoading: searchLoading} = useAPI(
        ['searchTalentForMessaging', {...searchParams, ...defaultParams}],
        {enabled: debouncedQuery.length >= 2}
    )

    // Exclude talent already in the matched list so you can only add net-new people
    const results = useMemo(() => {
        if (!searchData?.users) return []
        return searchData.users.map(mapUser).filter(
            t => !matches.find(m => m.id === t.id)
        )
    }, [searchData, matches])

    return (
        <div className="border border-t-0 border-gray-200">
            <button
                onClick={() => setOpen(p => !p)}
                className="w-full flex items-center gap-2 py-2.5 px-3.5 text-[13px] font-semibold text-indigo-600 hover:bg-indigo-50/50 transition-colors bg-white cursor-pointer"
            >
                <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                    <line x1="12" y1="5" x2="12" y2="19"/>
                    <line x1="5" y1="12" x2="19" y2="12"/>
                </svg>
                Add talent not in this list
                <svg
                    width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2"
                    viewBox="0 0 24 24"
                    className={cx('ml-auto transition-transform', open && 'rotate-180')}
                >
                    <polyline points="6,9 12,15 18,9"/>
                </svg>
            </button>
            {open && (
                <div className="border-t border-gray-200">
                    <div className="p-3">
                        <input
                            autoFocus
                            className="am-search w-full py-2 px-3 pl-8 border border-gray-200 text-[13px] bg-white outline-none focus:border-indigo-600 transition-colors"
                            placeholder="Search talent by name…"
                            value={query}
                            onChange={e => setQuery(e.target.value)}
                        />
                    </div>
                    <div className="overflow-y-auto am-scroll border-t border-gray-100" style={{maxHeight: '210px'}}>
                        {debouncedQuery.length < 2 ? (
                            <div className="py-5 text-center text-gray-400 text-[13px]">
                                Type at least 2 characters to search
                            </div>
                        ) : searchLoading ? (
                            <div className="py-5 text-center text-gray-400 text-[13px]">Searching…</div>
                        ) : results.length === 0 ? (
                            <div className="py-5 text-center text-gray-400 text-[13px]">No results found</div>
                        ) : results.map((t, idx) => {
                            const isAdded = customAdded.has(t.id)
                            return (
                                <div
                                    key={t.id}
                                    onClick={() => onToggleCustomAdd(t)}
                                    className={cx(
                                        'flex items-center gap-3 py-2.5 px-3.5 border-b border-gray-100 last:border-b-0 cursor-pointer select-none transition-colors',
                                        isAdded ? 'bg-emerald-50' : 'hover:bg-gray-50'
                                    )}
                                >
                                    <Checkbox checked={isAdded}/>
                                    <UserImageCircle user={t} size={32}/>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-[13px] font-medium truncate">{t.name}</div>
                                        <div className="text-[11.5px] text-gray-400">
                                            {t.disc}{t.loc ? ` · ${t.loc}` : ''}
                                        </div>
                                    </div>
                                    {isAdded && (
                                        <span className="shrink-0 text-[10px] font-semibold text-emerald-700 bg-emerald-100 border border-emerald-200 px-1.5 py-0.5">
                                            Added
                                        </span>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
        </div>
    )
}

// ─── Recipient List ───────────────────────────────────────────────────────────

const RecipientList = ({
    matches = [],
    matchLabel,
    excluded,
    onToggleExclude,
    isLoading = false,
    search = '',
    onSearchChange,
}) => {
    const [showExcludedOnly, setShowExcludedOnly] = useState(false)

    // Only the excluded-only toggle is applied client-side; text search goes through the API
    const displayedMatches = useMemo(
        () => showExcludedOnly ? matches.filter(t => excluded.has(t.id)) : matches,
        [matches, showExcludedOnly, excluded]
    )

    const excludedCount = excluded.size

    return (
        <div className="border border-gray-200">

            {/* Header */}
            <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border-b border-gray-200">
                <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500 truncate pr-2">
                    {matchLabel || `${matches.length} matched`}
                </span>
                {excludedCount > 0 && (
                    <button
                        onClick={() => setShowExcludedOnly(e => !e)}
                        className={cx(
                            'shrink-0 text-[11px] font-semibold px-2 py-0.5 border transition-colors',
                            showExcludedOnly
                                ? 'text-red-600 border-red-300 bg-red-50'
                                : 'text-gray-400 border-gray-200 hover:text-red-500 hover:border-red-200'
                        )}
                    >
                        {excludedCount} excluded{showExcludedOnly ? ' — show all ×' : ''}
                    </button>
                )}
            </div>

            {/* Search */}
            <div className="px-3 py-2 border-b border-gray-100">
                <input
                    className="am-search w-full py-2 px-3 pl-8 border border-gray-200 text-[13px] bg-white outline-none focus:border-indigo-600 transition-colors"
                    placeholder="Search by name…"
                    value={search}
                    onChange={e => onSearchChange?.(e.target.value)}
                />
            </div>

            {/* Rows */}
            <div className="overflow-y-auto am-scroll" style={{maxHeight: '300px'}}>
                {isLoading ? (
                    <div className="py-6 text-center text-gray-400 text-[13px]">Searching…</div>
                ) : displayedMatches.length === 0 ? (
                    <div className="py-6 text-center text-gray-400 text-[13px]">
                        {showExcludedOnly ? 'No excluded talent' : 'No results matched your search'}
                    </div>
                ) : displayedMatches.map((t, idx) => {
                    const isExcluded = excluded.has(t.id)
                    return (
                        <div
                            key={t.id}
                            onClick={() => onToggleExclude(t.id)}
                            className={cx(
                                'flex items-center gap-3 py-2.5 px-3.5 border-b border-gray-100 last:border-b-0 cursor-pointer select-none transition-colors',
                                isExcluded ? 'bg-gray-50 hover:bg-gray-100' : 'hover:bg-indigo-50/40'
                            )}
                        >
                            <Checkbox checked={!isExcluded}/>
                            <UserImageCircle user={t} size={32}/>
                            <div className="flex-1 min-w-0">
                                <div className={cx('text-[13px] font-medium truncate', isExcluded && 'line-through text-gray-400')}>
                                    {t.name}
                                </div>
                                <div className="text-[11.5px] text-gray-400">
                                    {t.disc}{t.loc ? ` · ${t.loc}` : ''}
                                </div>
                            </div>
                            {isExcluded && (
                                <span className="shrink-0 text-[10px] font-semibold text-red-500 bg-red-50 border border-red-200 px-1.5 py-0.5">
                                    Excluded
                                </span>
                            )}
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

// ─── AdminMessageNew ──────────────────────────────────────────────────────────

const AdminMessageNew = () => {
    const navigate = useNavigate()

    const [step, setStep] = useState(1)

    // ─── Step 1 form state ────────────────────────────────────────────────────
    const formRef = useRef(null)
    const [formModel, setFormModel] = useState({
        title: '',
        body: '',
        sender: null,
        category: null,
        is_urgent: false,
        action: null,
        default_action: null,
        client_id: null,
        position_id: null,
    })

    // Sender options — fetched from API, falls back to defaults while loading
    const {data: notifParams} = useAPI(['fetchNotificationParams'])
    const senderOptions = useMemo(() => {
        const senders = notifParams?.notification_senders ?? DEFAULT_SENDERS
        const defaultEmails = DEFAULT_SENDERS.map(s => s.email)
        return [
            ...senders.filter(s => defaultEmails.includes(s.email)),
            ...senders.filter(s => !defaultEmails.includes(s.email)),
        ]
    }, [notifParams])

    // Client search — debounced to avoid hammering the API
    const [clientSearch, setClientSearch] = useState('')
    const [debouncedClientSearch, setDebouncedClientSearch] = useState('')

    useEffect(() => {
        const t = setTimeout(() => setDebouncedClientSearch(clientSearch), 350)
        return () => clearTimeout(t)
    }, [clientSearch])

    const clientParams = useMemo(() => {
        const params = {}
        if (debouncedClientSearch) params.search = debouncedClientSearch
        return params
    }, [debouncedClientSearch])

    const {
        data: clientsData,
        isLoading: clientsLoading,
        error: clientsError,
    } = useAPI(
        ['fetchClients', clientParams],
        {enabled: CLIENT_CATEGORIES.includes(formModel.category)}
    )

    // Position search — debounced; only active once a client is selected
    const [positionSearch, setPositionSearch] = useState('')
    const [debouncedPositionSearch, setDebouncedPositionSearch] = useState('')

    useEffect(() => {
        const t = setTimeout(() => setDebouncedPositionSearch(positionSearch), 350)
        return () => clearTimeout(t)
    }, [positionSearch])

    const positionParams = useMemo(() => {
        const params = {client_id: formModel.client_id}
        if (debouncedPositionSearch) params.search = debouncedPositionSearch
        if (formModel.category === 2) params.posts_on_talent_job_board = 1
        return params
    }, [formModel.client_id, formModel.category, debouncedPositionSearch])

    const {
        data: positionsData,
        isLoading: positionsLoading,
        error: positionsError,
    } = useAPI(
        ['fetchProjectPositions', positionParams],
        {enabled: CLIENT_CATEGORIES.includes(formModel.category) && !!formModel.client_id}
    )
    // Dedicated handler for category card selection — bypasses the Form's onChange
    // pipe to prevent formsy re-renders from overwriting the selection.
    const handleCategoryChange = async (category) => {
        let action  = null;
        if (CLIENT_CATEGORIES.includes(category)) {
            action = 1;
        }
        console.log('handleCategoryChange', {category, action})
        await setFormModel({ ...formModel, category })
        await formRef.current?.updateInputsWithValue({
            ...formModel,
            category,
            action,
            client_id: null,
            position_id: null,
        }, false)

    }

    const handleFormChange = useCallback((changes) => {
        setFormModel(prev => {
            let next = {...prev}
            for (const [key, value] of Object.entries(changes)) {
                next = TB.set(next, key, value)
            }
            // Reset downstream fields when a parent field changes
            if (changes.hasOwnProperty('client_id') && prev.client_id !== changes.client_id) {
                next.position_id = null
            }
            return next
        })
    }, [])

    // ─── Step 2+ state ────────────────────────────────────────────────────────
    const [audiences, setAudiences] = useState(['all'])
    const [selDiscIds, setSelDiscIds] = useState([])

    // Location model for the "By Geography" audience type. Mirrors the shape that
    // TalentLocationRadioSelect.onChange emits (including full-location fields when
    // includeFullLocation is set). The model is sent to both the preview search and
    // the notification payload so the backend can apply fine-grained filtering.
    const [locationModel, setLocationModel] = useState({
        location:  TB.locationGlobalSetting(), // default: Global (searches immediately, no restriction)
        regions:   [],
        latitude:  null,
        longitude: null,
        address:   null,
        // Structured location fields emitted by TalentLocationRadioSelect when includeFullLocation=true.
        // Used to build prioritized filter arrays (cities > states > countries) for proximity mode.
        city:      null,
        state:     null,
        country:   null,
    })
    const [selTalent, setSelTalent] = useState(new Set())
    const [selTalentObjects, setSelTalentObjects] = useState({})
    // excluded: IDs unchecked from the matched list; customAddedTalent: full objects hand-added outside the filter
    const [excluded, setExcluded] = useState(new Set())
    const [customAddedTalent, setCustomAddedTalent] = useState([])
    const [showModal, setShowModal] = useState(false)
    // Pending discipline change — set when exclusions exist and user needs to confirm
    const [pendingDiscChange, setPendingDiscChange] = useState(null)

    // ─── Pagination for audience-filtered results ─────────────────────────────
    const [audiencePage, setAudiencePage] = useState(1)
    const [audienceRoster, setAudienceRoster] = useState([])
    const [rosterResetKey, setRosterResetKey] = useState(0)

    const toggleSet = useCallback((setter, key) => setter(prev => {
        const next = new Set(prev)
        next.has(key) ? next.delete(key) : next.add(key)
        return next
    }), [])

    // Recipient list search — debounced and sent as a param to searchTalentForMessaging
    const [recipientSearch, setRecipientSearch]             = useState('')
    const [debouncedRecipientSearch, setDebouncedRecipientSearch] = useState('')

    useEffect(() => {
        const t = setTimeout(() => setDebouncedRecipientSearch(recipientSearch), 350)
        return () => clearTimeout(t)
    }, [recipientSearch])

    // Reset to page 1 when the name search changes so results always start from the top
    useEffect(() => {
        setAudiencePage(1)
    }, [debouncedRecipientSearch])

    // Reset audience pagination + exclusions + search; custom-added talent is always preserved
    const resetAudienceSelections = useCallback(() => {
        setExcluded(new Set())
        setAudiencePage(1)
        setAudienceRoster([])
        setRecipientSearch('')
        setRosterResetKey(k => k + 1)
    }, [])

    const handleToggleCustomAdd = useCallback((talent) => {
        setCustomAddedTalent(prev =>
            prev.find(t => t.id === talent.id)
                ? prev.filter(t => t.id !== talent.id)
                : [...prev, talent]
        )
    }, [])

    const handleSpecificTalentToggle = useCallback((talent) => {
        setSelTalent(prev => {
            const next = new Set(prev)
            next.has(talent.id) ? next.delete(talent.id) : next.add(talent.id)
            return next
        })
        setSelTalentObjects(prev => {
            if (prev[talent.id]) {
                const {[talent.id]: _, ...rest} = prev
                return rest
            }
            return {...prev, [talent.id]: talent}
        })
    }, [])

    // Reset page + roster whenever filter criteria change (not when page increments)
    const handleAudienceChange = useCallback((key) => {
        setAudiences(prev => {
            if (key === 'all') {
                if (prev.includes('all')) return prev
                return ['all']
            }
            const withoutAll = prev.filter(k => k !== 'all')
            if (withoutAll.includes(key)) {
                const next = withoutAll.filter(k => k !== key)
                return next.length === 0 ? ['all'] : next
            }
            return [...withoutAll, key]
        })
        resetAudienceSelections()
    }, [resetAudienceSelections])

    const applyDiscChange = useCallback((ids) => {
        setSelDiscIds(ids)
        resetAudienceSelections()
    }, [resetAudienceSelections])

    const handleDiscChange = useCallback((ids) => {
        if (excluded.size > 0) {
            // Ask for confirmation before discarding exclusions
            setPendingDiscChange(ids)
        } else {
            applyDiscChange(ids)
        }
    }, [excluded, applyDiscChange])

    // TalentLocationRadioSelect fires partial updates (e.g. just {latitude, longitude} when
    // a proximity pin is dropped). Merge them into locationModel and reset the roster so
    // the new location filter is applied from page 1.
    const handleLocationChange = useCallback((updates) => {
        setLocationModel(prev => ({...prev, ...updates}))
        resetAudienceSelections()
    }, [resetAudienceSelections])

    // ─── API params driven by audience + filters + page ───────────────────────

    // Extra search params added based on message category + selected position
    const positionTargetingParams = useMemo(() => {
        if (formModel.category === 2 && formModel.position_id) {
            return {matching_position_id: formModel.position_id}
        }
        if (formModel.category === 3 && formModel.position_id) {
            return {shortlisted_for_position_id: formModel.position_id}
        }
        return {}
    }, [formModel.category, formModel.position_id])

    // True when the geo audience type is active AND in a state that should trigger a fetch.
    // — Global:    always enabled — searches all talent with no location restriction by default
    // — Regional:  enabled once at least one region checkbox is checked
    // — Proximity: enabled once the user has dropped a location pin (lat + lng required)
    const geoAudienceEnabled = useMemo(() => {
        if (!audiences.includes('geo')) return false
        const {location, regions, latitude, longitude} = locationModel
        if (location === TB.locationGlobalSetting())  return true               // default: search immediately
        if (location === TB.locationRegionalSetting()) return regions?.length > 0
        if (location > 3) return !!(latitude && longitude)                      // proximity needs a pin
        return false
    }, [audiences, locationModel])

    const audienceParams = useMemo(() => {
        const params = {page: audiencePage, ...positionTargetingParams}
        if (audiences.includes('discipline') && selDiscIds.length) {
            params.approved_disciplines = selDiscIds
        }
        // Send location params when geo is active and configured.
        // visibility_setting_location drives the backend's location-matching logic.
        // For proximity mode, also send a prioritised location array (city > state > country)
        // so the backend can apply fine-grained filtering in addition to the radius.
        if (geoAudienceEnabled) {
            params.visibility_setting_location = locationModel.location
            params.address = locationModel.address;
            if (locationModel.regions?.length)   params.regions   = locationModel.regions
            if (locationModel.latitude  != null) params.latitude  = locationModel.latitude
            if (locationModel.longitude != null) params.longitude = locationModel.longitude

            // Proximity-only: derive a single-item array from the most specific location field
            if (locationModel.location > 3) {
                if (locationModel.city)         params.cities    = [locationModel.city]
                else if (locationModel.state)   params.states    = [locationModel.state]
                else if (locationModel.country) params.countries = [locationModel.country]
            }
        }
        if (debouncedRecipientSearch) {
            params.search = debouncedRecipientSearch
        }
        return params
    }, [audiences, selDiscIds, geoAudienceEnabled, locationModel, audiencePage, positionTargetingParams, debouncedRecipientSearch])

    // Only fetch once the user has actually picked a filter for the chosen audience type
    const audienceEnabled = useMemo(() => {
        if (audiences.every(a => a === 'specific')) return false
        if (audiences.includes('all')) return true
        if (audiences.includes('discipline') && selDiscIds.length > 0) return true
        if (geoAudienceEnabled) return true
        return false
    }, [audiences, selDiscIds, geoAudienceEnabled])

    const {data: audienceData, isLoading: audienceLoading} = useAPI(
        ['searchTalentForMessaging', {...audienceParams, ...defaultParams}],
        {enabled: audienceEnabled}
    )

    // True while the request that determines the audience size is in flight.
    // Page > 1 loads (load more) only append rows — they never change the count,
    // so they don't put the count into a loading state.
    const audienceCountLoading = audienceEnabled && audienceLoading && audiencePage === 1

    // Accumulate audience roster across pages; use server current_page to decide.
    // rosterResetKey forces this to re-run after resetAudienceSelections clears the roster,
    // even when audienceData is a cached React Query result (same reference, no normal re-fire).
    useEffect(() => {
        if (!audienceData?.users) return
        const mapped = audienceData.users.map(mapUser)
        if (audienceData.current_page === 1) {
            setAudienceRoster(mapped)
        } else {
            setAudienceRoster(prev => [...prev, ...mapped])
        }
    }, [audienceData, rosterResetKey])

    // Persist the last known audience total so the "Sending to X" count never
    // flickers to 0 while a new search request is in-flight. Updated whenever
    // audienceData resolves; skipped when audienceData is undefined (loading).
    const [stableAudienceTotal, setStableAudienceTotal] = useState(0)
    useEffect(() => {
        if (!audienceData) return
        // Prefer base_total (true audience ignoring name search) when present
        setStableAudienceTotal(audienceData.base_total ?? audienceData.total ?? 0)
    }, [audienceData])

    const hasMoreAudience = audienceData
        ? (audienceData.current_page ?? 1) < (audienceData.last_page ?? 1)
        : false

    // ─── Dynamic audience label ───────────────────────────────────────────────

    const audienceMatchLabel = useMemo(() => {
        // When a name search is active the API returns a narrowed `total` (matching the search
        // query) alongside a `base_total` (the full audience regardless of the name search).
        // The label should always describe the real audience size, not the search-filtered subset.
        // While the count request is in flight, say so instead of showing a misleading 0.
        const total = audienceData?.base_total ?? audienceData?.total ?? 0
        const noun = audienceCountLoading
            ? 'Counting talent'
            : `${total} talent`
        const parts = []
        if (audiences.includes('discipline') && selDiscIds.length) {
            const names = selDiscIds
                .map(id => allDisciplines.find(d => d.id === id)?.name)
                .filter(Boolean)
            const formatted = names.length === 1
                ? names[0]
                : names.length === 2
                    ? `${names[0]} and ${names[1]}`
                    : `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
            parts.push({type: 'discipline', text: formatted})
        }
        if (geoAudienceEnabled) {
            const {location, regions, address} = locationModel
            let geoText = ''
            if (location === TB.locationRegionalSetting()) {
                // Resolve region names from laravel.regions for a readable label
                const regionNames = [...(laravel.regions || [])]
                    .filter(r => regions.includes(r.id))
                    .map(r => r.name)
                geoText = regionNames.length ? regionNames.join(', ') : 'selected regions'
            } else if (location > 3) {
                // Proximity: extract mileage from the laravel settings map
                const mileSetting = laravel.project_visibility_location_settings?.[location]
                const miles = mileSetting ? parseInt(mileSetting.replace(/^\D+/g, '')) : null
                geoText = miles ? `within ${miles} miles` : 'nearby'
                if (address) geoText += ` of ${address}`
            }
            if (geoText) parts.push({type: 'geo', text: geoText})
        }
        if (parts.length === 0) return `${noun} on the platform`
        if (parts.length === 1) {
            return parts[0].type === 'discipline'
                ? `${noun} matching ${parts[0].text} roles`
                : `${noun} in ${parts[0].text}`
        }
        return `${noun} matching ${parts[0].text} roles in ${parts[1].text}`
    }, [audiences, selDiscIds, geoAudienceEnabled, locationModel, audienceData, audienceCountLoading])

    // ─── Derived targeting state (shared across all non-specific audiences) ─────

    // Custom-added talent that are NOT already covered by the filter results
    const customAddedOutside = useMemo(
        () => customAddedTalent.filter(t => !audienceRoster.find(m => m.id === t.id)),
        [customAddedTalent, audienceRoster]
    )

    // Set form of customAddedTalent IDs for O(1) membership checks in AddTalentPanel
    const customAddedSet = useMemo(
        () => new Set(customAddedTalent.map(t => t.id)),
        [customAddedTalent]
    )

    // Server total (all matching, not just loaded pages) minus explicit exclusions + custom additions
    const sendingCount = useMemo(() => {
        const onlySpecific = audiences.length === 1 && audiences[0] === 'specific'
        if (onlySpecific) return selTalent.size

        // Use stableAudienceTotal instead of reading audienceData directly.
        // audienceData becomes undefined while a new search request is in-flight,
        // which would cause this to flicker to 0. stableAudienceTotal always holds
        // the last successfully resolved value (base_total when a name search is
        // active, or total when no name search is active).
        const serverTotal = stableAudienceTotal

        // When specific is combined with other audiences, count selected talent not already in the roster
        const specificOutside = audiences.includes('specific')
            ? [...selTalent].filter(id => !audienceRoster.find(t => t.id === id)).length
            : 0

        return Math.max(0, serverTotal - excluded.size + customAddedOutside.length + specificOutside)
    }, [audiences, selTalent, stableAudienceTotal, excluded, customAddedOutside, audienceRoster])

    // ─── Recipient summary for step 4 ────────────────────────────────────────

    const recipientCfg = useMemo(() => {
        if (audiences.length === 1 && audiences[0] === 'specific') {
            const picked = [...selTalent].map(id => selTalentObjects[id]).filter(Boolean)
            return {
                n: picked.length,
                summary: `Sending to ${picked.length} hand-picked talent`,
                samples: picked.slice(0, 4),
            }
        }
        return {
            n: sendingCount,
            summary: audienceMatchLabel,
            samples: audienceRoster.filter(t => !excluded.has(t.id)).slice(0, 4),
        }
    }, [audiences, selTalent, selTalentObjects, audienceRoster, audienceMatchLabel, excluded, sendingCount])

    // ─── Build POST payload aligned with SystemNotificationsController@store ────
    const buildFilters = useCallback(() => {
        const isAnnouncement = formModel.category === 1
        const onlySpecific   = audiences.length === 1 && audiences[0] === 'specific'

        const filters = {}

        // Announcement-only audience filters
        if (isAnnouncement) {
            if (selDiscIds.length) filters.discipline_ids = selDiscIds
            // Geo location: append all locationModel attributes so the backend can
            // apply regional or proximity filtering via apply_request_location_matching_to_filters.
            // For proximity, also include the prioritised location array (city > state > country).
            if (audiences.includes('geo') && geoAudienceEnabled) {
                filters.visibility_setting_location = locationModel.location
                if (locationModel.regions?.length)   filters.regions   = locationModel.regions
                if (locationModel.latitude  != null) filters.latitude  = locationModel.latitude
                if (locationModel.longitude != null) filters.longitude = locationModel.longitude
                if (locationModel.address)           filters.address   = locationModel.address

                // Proximity-only: send the most specific available location identifier
                if (locationModel.location > 3) {
                    if (locationModel.city)         filters.cities    = [locationModel.city]
                    else if (locationModel.state)   filters.states    = [locationModel.state]
                    else if (locationModel.country) filters.countries = [locationModel.country]
                }
            }
        }

        if (onlySpecific) {
            // Announcement + Specific Talent: use only_user_ids so the backend sends
            // exclusively to these users rather than treating them as additions to a filter.
            if (isAnnouncement) {
                filters.only_user_ids = [...selTalent]
            } else {
                filters.added_user_ids = [...selTalent]
            }
        } else {
            // Filter-based audience: send excluded + any additions outside the filter
            if (excluded.size) filters.exluded_user_ids = [...excluded]  // note: backend typo

            const specificOutsideRoster = audiences.includes('specific')
                ? [...selTalent].filter(id => !audienceRoster.find(t => t.id === id))
                : []
            const addedIds = [...new Set([...customAddedTalent.map(t => t.id), ...specificOutsideRoster])]
            if (addedIds.length) filters.added_user_ids = addedIds
        }
        return filters
    })
    const buildPayload = useCallback(() => {

        const filters = buildFilters();

        const sender = senderOptions.find(s => s.email === formModel.sender)

        const payload = {
            title:        formModel.title,
            body:         formModel.body,
            category:     formModel.category,
            is_urgent:    formModel.is_urgent,
            sender_name:  sender.name,
            sender_email: sender.email,
            filters:      Object.keys(filters).length ? filters : null,
        }

        if (CLIENT_CATEGORIES.includes(formModel.category)) {
            payload.action              = 1
            payload.project_position_id = formModel.position_id
        } else if (formModel.action && formModel.action !== 1) {
            payload.action = formModel.action
        }

        return payload
    }, [formModel, selDiscIds, locationModel, geoAudienceEnabled, excluded, audiences, selTalent, audienceRoster, customAddedTalent])


    const previewMessage = useMemo(() => {
        let preview = {...formModel}
        preview.created_at = TB.moment();
        preview.filters = buildFilters();
        preview.system_notification = {...preview}

        if (formModel.client_id) {
            preview.client = TB.find((clientsData?.data || []), ['id', formModel.client_id])
        }
        if (formModel.position_id) {
            preview.project_position_id = formModel.position_id
            preview.project_position = TB.find((positionsData?.data || []), ['id', formModel.position_id]);
            if (preview.project_position?.project_name) {
                preview.project = {
                    name: preview.project_position.project_name,
                    id: preview.project_position.project_id
                }
            }
        }

        if (formModel.sender) {
            const sender = senderOptions.find(s => s.email === formModel.sender)
            preview.sender_name = sender?.name ?? null
        }
        if (formModel.action) {
            preview.action_title = TB.find(ANNOUNCEMENT_ACTIONS, ['value', formModel.action])?.label
        }
        if (formModel.action === 1 && !CLIENT_CATEGORIES.includes(formModel.category)) {
            preview.action = null;
        }

        return preview
    }, [
        formModel,
        selDiscIds,
        locationModel,
        geoAudienceEnabled,
        excluded, audiences,
        selTalent, audienceRoster,
        customAddedTalent,
        step
    ]);

    const sendMessageMutation = useMutation(async () => {
        const payload  = buildPayload()
        const request  = TB.request(TB.api_endpoint('/api/system-notifications/create'), payload)
        const response = await request.post().catch(e => { throw new Error(e.message || 'Request failed') })

        if (!TB.has(response, 'success.data.notification')) {
            throw new Error('Something went wrong. Please try again.')
        }

        return response.success.data
    })

    const handleCloseModal = useCallback(() => {
        setShowModal(false)
        sendMessageMutation.reset()
    }, [sendMessageMutation])

    const handleSend = () => {
        handleCloseModal()
        navigate('../messaging')
    }

    return (
        <div className="flex flex-col flex-grow items-stretch">
            <style>{`
                .am-scroll::-webkit-scrollbar, .am-page::-webkit-scrollbar { width: 4px; }
                .am-scroll::-webkit-scrollbar-thumb, .am-page::-webkit-scrollbar-thumb { background: #e4e7ed; border-radius: 4px; }
                .am-select { appearance: none; background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%239BA5B7' d='M6 8L1 3h10z'/%3E%3C/svg%3E") no-repeat right 12px center white; }
                .am-search { background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' fill='none' stroke='%239BA5B7' stroke-width='2' viewBox='0 0 24 24'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cline x1='21' y1='21' x2='16.65' y2='16.65'/%3E%3C/svg%3E") no-repeat 10px center white; }
            `}</style>

            <div className="flex flex-col w-full flex-grow bg-gray-100 text-gray-900 text-sm">

                {/* Header */}
                <div className="px-7 py-5 bg-black flex items-center justify-between shrink-0">
                    <div>
                        <h1 className="text-lg font-bold tracking-tight text-white">Compose Message</h1>
                        <p className="text-[13px] text-gray-400 mt-0.5">Compose a message and send it to the right talent</p>
                    </div>
                    <button
                        onClick={() => navigate('../messaging')}
                        className="px-5 py-2.5 bg-transparent text-white border border-gray-600 text-[13.5px] font-medium cursor-pointer hover:bg-gray-800 transition-colors"
                    >
                        Cancel
                    </button>
                </div>

                {/* Scrollable body */}
                <div className="py-6 px-7">
                    <div className={TB.mapClass(" mx-auto", {
                        "max-w-[720px]": step !== 4,
                        "max-w-[980px]": step === 4,
                    })}>
                        <StepsBar step={step}/>

                        {/* Card wrapper */}
                        <div className="bg-white border border-gray-200 p-6 mb-4">

                            {/* Step 1 — Message Basics */}
                            {step === 1 && (
                                <>
                                    <h3 className="text-[15px] font-bold mb-1">Message Basics</h3>
                                    <p className="text-[13px] text-gray-500 mb-5">
                                        Add your message content and details
                                    </p>

                                    <Form
                                        ref={formRef}
                                        model={formModel}
                                        onChange={handleFormChange}
                                        submitButton={false}
                                        className="[&>.form-container]:pt-0"
                                        fields={[]}
                                    >
                                        {(() => {
                                            // Progressive enable: each field unlocks only after the one above it is filled.
                                            const categoryFilled         = formModel.category != null
                                            const isClientCategory       = CLIENT_CATEGORIES.includes(formModel.category)
                                            const isAnnouncementCategory = formModel.category === 1
                                            const clientFilled           = isClientCategory && formModel.client_id != null
                                            const positionFilled         = clientFilled && formModel.position_id != null
                                            const clientOrActionFilled   = categoryFilled && (
                                                (isAnnouncementCategory && formModel.action != null) ||
                                                (isClientCategory       && positionFilled)
                                            )
                                            const senderFilled = clientOrActionFilled && !!formModel.sender
                                            const titleFilled  = senderFilled && !!formModel.title
                                            const bodyFilled   = titleFilled && !!formModel.body

                                            return (
                                                <div className="flex flex-col gap-4">

                                                    {/* 1. Category — always enabled */}
                                                    <div>
                                                        <label className={labelBase}>Choose Category <span className="text-red-400">*</span></label>
                                                        <div className="flex flex-col gap-2.5">
                                                            {MESSAGE_CATEGORIES.map(({value, label, description, icon: Icon, colorKey}) => {
                                                                const color = TB.config(colorKey)
                                                                const selected = formModel.category === value
                                                                return (
                                                                    <div
                                                                        key={value}
                                                                        onClick={() => handleCategoryChange(value)}
                                                                        style={selected ? {borderColor: color, backgroundColor: `${color}0d`} : {}}
                                                                        className={cx(
                                                                            'border-[1.5px] p-3.5 cursor-pointer flex gap-3 items-start transition-colors',
                                                                            selected ? '' : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50/50'
                                                                        )}
                                                                    >
                                                                        <div
                                                                            className="w-9 h-9 flex items-center justify-center shrink-0 text-[17px]"
                                                                            style={{backgroundColor: `${color}1a`, color}}
                                                                        >
                                                                            <Icon/>
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <h4 className="text-[13px] font-semibold mb-0.5 text-gray-900">{label}</h4>
                                                                            <p
                                                                                className={cx('text-xs leading-snug', value !== 2 && 'text-gray-500')}
                                                                            >{description}</p>
                                                                        </div>
                                                                        {/* Radio dot indicator */}
                                                                        <div
                                                                            className="w-4 h-4 rounded-full border-[1.5px] shrink-0 flex items-center justify-center mt-0.5 transition-colors"
                                                                            style={selected
                                                                                ? {borderColor: color, backgroundColor: color}
                                                                                : {borderColor: '#d1d5db', backgroundColor: 'white'}
                                                                            }
                                                                        >
                                                                            {selected && <div className="w-1.5 h-1.5 rounded-full bg-white"/>}
                                                                        </div>
                                                                    </div>
                                                                )
                                                            })}
                                                        </div>
                                                    </div>

                                                    {/* Warning shown when "Potential Talent Matches for Role" is selected */}
                                                    {formModel.category === 2 && (
                                                        <Alert
                                                            type="warning"
                                                            showIcon
                                                            message={<span className="font-semibold">Job Board Requirement</span>}
                                                            description="This message type only reaches open positions with an active Talent Job Board listing. Make sure your project is posted before continuing."
                                                        />
                                                    )}

                                                    {/* 2. Action — editable for Announcement category */}
                                                    {isAnnouncementCategory ? (
                                                        <FormInputComponent
                                                            fieldName="action"
                                                            value={formModel.action}
                                                            label="Message Button"
                                                            type="antd-input"
                                                            required={true}
                                                            disabled={!categoryFilled}
                                                        >
                                                            <Select
                                                                placeholder="Select an action"
                                                                style={{width: '100%'}}
                                                                disabled={!categoryFilled}
                                                            >
                                                                {ANNOUNCEMENT_ACTIONS.map(({label, value}) => (
                                                                    <Select.Option key={value} value={value}>{label}</Select.Option>
                                                                ))}
                                                            </Select>
                                                        </FormInputComponent>
                                                    ) : (
                                                        categoryFilled && isClientCategory && (
                                                            <FormInputComponent
                                                                fieldName="action"
                                                                value={formModel.action}
                                                                label={<span>Message Button <span className="text-sm italic font-semibold text-danger">
                                                                    Not editable
                                                                </span></span>}
                                                                type="antd-input"
                                                                // required={true}
                                                                disabled={true}
                                                            >
                                                                <Select
                                                                    style={{width: '100%'}}
                                                                    disabled={true}
                                                                >
                                                                    <Select.Option value={1}>
                                                                        {formModel.category === 2 ? "Job Details Page" : 'Opportunity Dashboard'}
                                                                    </Select.Option>
                                                                </Select>
                                                            </FormInputComponent>
                                                        )
                                                    )}

                                                    {/* 3. Client — shown for Project / Shortlisted categories */}
                                                    {isClientCategory && (
                                                        <FormInputComponent
                                                            fieldName="client_id"
                                                            value={formModel.client_id}
                                                            label="Select Client"
                                                            type="antd-input"
                                                            required={true}
                                                            disabled={!categoryFilled}
                                                        >
                                                            <Select
                                                                showSearch
                                                                filterOption={false}
                                                                onSearch={setClientSearch}
                                                                placeholder="Search for a client…"
                                                                style={{width: '100%'}}
                                                                loading={clientsLoading}
                                                                disabled={!categoryFilled}
                                                                notFoundContent={
                                                                    clientsError
                                                                        ? <span className="text-red-500 text-xs">Error loading clients</span>
                                                                        : clientsLoading
                                                                            ? <Spin size="small"/>
                                                                            : 'No clients found'
                                                                }
                                                            >
                                                                {(clientsData?.data || []).map(client => (
                                                                    <Select.Option key={client.id} value={client.id}>
                                                                        {client.name}
                                                                    </Select.Option>
                                                                ))}
                                                            </Select>
                                                        </FormInputComponent>
                                                    )}

                                                    {/* 4. Role (Project Position) — shown for client categories, enabled once client is selected */}
                                                    {isClientCategory && (
                                                        <FormInputComponent
                                                            fieldName="position_id"
                                                            value={formModel.position_id}
                                                            label="Select Active Role"
                                                            type="antd-input"
                                                            required={true}
                                                            disabled={!clientFilled}
                                                        >
                                                            <Select
                                                                showSearch
                                                                filterOption={false}
                                                                onSearch={setPositionSearch}
                                                                placeholder="Search for a role at the selected client…"
                                                                style={{width: '100%'}}
                                                                loading={positionsLoading}
                                                                disabled={!clientFilled}
                                                                notFoundContent={
                                                                    positionsError
                                                                        ? <span className="text-red-500 text-xs">Error loading positions</span>
                                                                        : positionsLoading
                                                                            ? <Spin size="small"/>
                                                                            : 'No positions found'
                                                                }
                                                            >
                                                                {(positionsData?.data || []).map(position => (
                                                                    <Select.Option key={position.id} value={position.id}>
                                                                        <div>{position.title}</div>
                                                                        <div className="text-xs text-gray-500">
                                                                            {position.project_name} &middot; Created {TB.moment(position.created_at).format('MMM D, YYYY')}
                                                                        </div>
                                                                    </Select.Option>
                                                                ))}
                                                            </Select>
                                                        </FormInputComponent>
                                                    )}

                                                    {/* 4. Sender */}
                                                    <FormInputComponent
                                                        fieldName="sender"
                                                        value={formModel.sender}
                                                        label="Sender"
                                                        type="antd-input"
                                                        required={true}
                                                        disabled={!clientOrActionFilled}
                                                    >
                                                        <Select
                                                            placeholder="Select a sender"
                                                            disabled={!clientOrActionFilled}
                                                            loading={!notifParams}
                                                            notFoundContent={!notifParams ? <Spin size="small"/> : 'No senders found'}
                                                        >
                                                            {senderOptions.map(({name, email}) => (
                                                                <Select.Option key={email} value={email}>
                                                                    <div className="font-medium text-[13px]">{name}</div>
                                                                    <div className="text-xs text-gray-400">{email}</div>
                                                                </Select.Option>
                                                            ))}
                                                        </Select>
                                                    </FormInputComponent>

                                                    {/* 5. Title */}
                                                    <FormInputComponent
                                                        fieldName="title"
                                                        value={formModel.title}
                                                        label="Title"
                                                        type="antd-input"
                                                        required={true}
                                                        disabled={!senderFilled}
                                                    >
                                                        <Input
                                                            placeholder="Add a message title"
                                                            disabled={!senderFilled}
                                                        />
                                                    </FormInputComponent>

                                                    {/* 6. Body */}
                                                    <FormInputComponent
                                                        fieldName="body"
                                                        value={formModel.body}
                                                        label="Body"
                                                        placeholder="Write your message…"
                                                        type="editor"
                                                        required={true}
                                                        disabled={!titleFilled}
                                                        maxLength={10000}
                                                        tools={['bold', 'italic', 'underline', 'link', 'ol', 'ul']}
                                                    />

                                                    {/* 7. Urgent flag */}
                                                    <div
                                                        onClick={() => bodyFilled && handleFormChange({is_urgent: !formModel.is_urgent})}
                                                        className={cx(
                                                            'flex items-center justify-between gap-4 p-3.5 border-[1.5px] transition-colors',
                                                            !bodyFilled
                                                                ? 'opacity-40 pointer-events-none border-gray-200 bg-gray-50'
                                                                : formModel.is_urgent
                                                                    ? 'border-red-300 bg-red-50 cursor-pointer'
                                                                    : 'border-gray-200 bg-white cursor-pointer hover:border-gray-300 hover:bg-gray-50/50'
                                                        )}
                                                    >
                                                        <div>
                                                            <div className="flex items-center gap-2">
                                                                <span className={cx('text-[13px] font-semibold', formModel.is_urgent ? 'text-red-600' : 'text-gray-900')}>
                                                                    High Priority
                                                                </span>
                                                                {formModel.is_urgent && (
                                                                    <span className="text-[10px] font-bold uppercase tracking-wide text-red-500 bg-red-100 px-1.5 py-0.5">
                                                                        On
                                                                    </span>
                                                                )}
                                                            </div>
                                                            <p className="text-xs text-gray-500 mt-0.5">
                                                                Flag this message as urgent — recipients will see a high priority indicator
                                                            </p>
                                                        </div>
                                                        {/* Toggle switch */}
                                                        <div className={cx('w-11 h-6 rounded-full shrink-0 relative transition-colors', formModel.is_urgent ? 'bg-red-500' : 'bg-gray-200')}>
                                                            <span className={cx('absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform', formModel.is_urgent ? 'translate-x-5' : 'translate-x-0.5')}/>
                                                        </div>
                                                    </div>

                                                </div>
                                            )
                                        })()}
                                    </Form>
                                </>
                            )}

                            {/* Step 2 — Choose Audience */}
                            {step === 2 && (
                                <>
                                    {/* Categories 2 & 3 have a fixed, pre-determined audience */}
                                    {formModel.category === 2 || formModel.category === 3 ? (() => {
                                        const fixedLabel = formModel.category === 2 ? 'All Talent Matches' : 'Shortlisted Talent'
                                        const fixedDesc  = formModel.category === 2
                                            ? 'All talent whose role(s) and availability align with the selected position will be included automatically.'
                                            : 'All candidates shortlisted for the selected position start as included in the list.'
                                        return (
                                            <>
                                                <h3 className="text-[15px] font-bold mb-1">Audience</h3>
                                                <p className="text-[13px] text-gray-500 mb-5">
                                                    All candidates shortlisted for the selected position start as included in the list.
                                                </p>
                                                <div className="border-[1.5px] border-indigo-600 bg-indigo-50 p-3.5 flex gap-2.5 items-start">
                                                    <div className="w-8 h-8 flex items-center justify-center shrink-0 bg-indigo-100 stroke-indigo-600">
                                                        <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 7a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/>
                                                        </svg>
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="text-[13px] font-semibold mb-0.5">{fixedLabel}</h4>
                                                        <p className="text-xs text-gray-500 leading-snug">{fixedDesc}</p>
                                                    </div>
                                                    {/* Static checked indicator — not interactive */}
                                                    <div className="w-4 h-4 border-[1.5px] shrink-0 flex items-center justify-center mt-0.5 bg-indigo-600 border-indigo-600">
                                                        <svg width="10" height="10" fill="none" stroke="white" strokeWidth="2.5" viewBox="0 0 24 24">
                                                            <polyline points="20,6 9,17 4,12"/>
                                                        </svg>
                                                    </div>
                                                </div>
                                            </>
                                        )
                                    })() : (
                                        <>
                                            <h3 className="text-[15px] font-bold mb-1">Choose Audience</h3>
                                            <p className="text-[13px] text-gray-500 mb-5">
                                                Send to everyone, or narrow down by one or more filters below.
                                            </p>

                                            {/* ── All Talent (exclusive) ── */}
                                            {(() => {
                                                const t = AUDIENCE_TYPES.find(a => a.key === 'all')
                                                const isSelected = audiences.includes('all')
                                                return (
                                                    <div
                                                        onClick={() => handleAudienceChange('all')}
                                                        className={cx(
                                                            'border-[1.5px] p-3.5 cursor-pointer flex gap-2.5 items-start transition-colors',
                                                            isSelected ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/30'
                                                        )}
                                                    >
                                                        <div className={cx('w-8 h-8 flex items-center justify-center shrink-0', t.color)}>
                                                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                <path d={t.icon}/>
                                                            </svg>
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <h4 className="text-[13px] font-semibold mb-0.5">{t.label}</h4>
                                                            <p className="text-xs text-gray-500 leading-snug">{t.desc}</p>
                                                        </div>
                                                        {/* Radio indicator — exclusive selection */}
                                                        <div className={cx(
                                                            'w-4 h-4 rounded-full border-[1.5px] shrink-0 flex items-center justify-center transition-colors mt-0.5',
                                                            isSelected ? 'border-indigo-600' : 'border-gray-300 bg-white'
                                                        )}>
                                                            {isSelected && (
                                                                <div className="w-2 h-2 rounded-full bg-indigo-600"/>
                                                            )}
                                                        </div>
                                                    </div>
                                                )
                                            })()}

                                            {/* ── OR divider ── */}
                                            <div className="flex items-center gap-3 my-4">
                                                <div className="flex-1 border-t border-gray-200"/>
                                                <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400">or narrow by</span>
                                                <div className="flex-1 border-t border-gray-200"/>
                                            </div>

                                            {/* ── Combinable filter types ── */}
                                            <p className="text-[11px] text-gray-400 mb-2.5">These can be combined to further filter the list.</p>
                                            <div className="grid grid-cols-2 gap-2.5">
                                                {AUDIENCE_TYPES.filter(a => a.key !== 'all').map(({key, label, desc, color, icon, fullWidth}) => {
                                                    const isSelected = audiences.includes(key)
                                                    return (
                                                        <div
                                                            key={key}
                                                            onClick={() => handleAudienceChange(key)}
                                                            className={cx(
                                                                'border-[1.5px] p-3.5 cursor-pointer flex gap-2.5 items-start transition-colors',
                                                                fullWidth && 'col-span-2',
                                                                isSelected ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300 hover:bg-indigo-50/30'
                                                            )}
                                                        >
                                                            <div className={cx('w-8 h-8 flex items-center justify-center shrink-0', color)}>
                                                                <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                                                    <path d={icon}/>
                                                                </svg>
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <h4 className="text-[13px] font-semibold mb-0.5">{label}</h4>
                                                                <p className="text-xs text-gray-500 leading-snug">{desc}</p>
                                                            </div>
                                                            {/* Checkbox indicator — combinable */}
                                                            <div className={cx(
                                                                'w-4 h-4 border-[1.5px] shrink-0 flex items-center justify-center transition-colors mt-0.5',
                                                                isSelected ? 'bg-indigo-600 border-indigo-600' : 'border-gray-300 bg-white'
                                                            )}>
                                                                {isSelected && (
                                                                    <svg width="10" height="10" fill="none" stroke="white" strokeWidth="2.5" viewBox="0 0 24 24">
                                                                        <polyline points="20,6 9,17 4,12"/>
                                                                    </svg>
                                                                )}
                                                            </div>
                                                        </div>
                                                    )
                                                })}
                                            </div>
                                        </>
                                    )}
                                </>
                            )}

                            {/* Step 3 — Configure Targeting */}
                            {step === 3 && (
                                <>
                                    <h3 className="text-[15px] font-bold mb-1">Configure Targeting</h3>
                                    <p className="text-[13px] text-gray-500 mb-5">
                                        Choose exactly who receives this message
                                    </p>

                                    {/* ── Audience-specific filter controls ── */}
                                    {audiences.includes('discipline') && (
                                        <div className="mb-4">
                                            <label className={labelBase}>Select roles</label>
                                            <Select
                                                mode="multiple"
                                                allowClear
                                                style={{width: '100%'}}
                                                placeholder="Search and select roles…"
                                                options={disciplineOptions}
                                                value={selDiscIds}
                                                onChange={handleDiscChange}
                                                maxTagCount="responsive"
                                                filterOption={(input, option) => {
                                                    const q = input.toLowerCase()
                                                    const catName = (disciplineCategoryMap[option.value] || '').toLowerCase()
                                                    return option.label.toLowerCase().includes(q) || catName.includes(q)
                                                }}
                                            />
                                        </div>
                                    )}

                                    {audiences.includes('geo') && (
                                        <div className="mb-4">
                                            <label className={labelBase}>Location</label>
                                            {/*
                                              * TalentLocationRadioSelect drives three modes:
                                              *   Global    – no location restriction (default, treated as "not configured")
                                              *   Regional  – filter by one or more laravel.regions checkboxes
                                              *   Proximity – filter within X miles of a searched city/address
                                              *
                                              * onChange fires partial updates; handleLocationChange merges them
                                              * into locationModel and resets the roster so the API is re-queried.
                                              */}
                                            <TalentLocationRadioSelect
                                                value={locationModel.location}
                                                regions={locationModel.regions}
                                                proximity={locationModel.latitude ? {
                                                    latitude:  locationModel.latitude,
                                                    longitude: locationModel.longitude,
                                                    full:      locationModel.address,
                                                } : {}}
                                                onChange={handleLocationChange}
                                            />
                                        </div>
                                    )}

                                    {/* ── Specific talent picker ── */}
                                    {audiences.includes('specific') && (
                                        <div className={cx(audiences.length > 1 && 'mb-6')}>
                                            {audiences.length > 1 && (
                                                <label className={cx(labelBase, 'mb-2')}>Hand-pick specific talent</label>
                                            )}
                                            <TalentPickerList
                                                selected={selTalent}
                                                selectedObjects={selTalentObjects}
                                                onToggle={handleSpecificTalentToggle}
                                            />
                                        </div>
                                    )}

                                    {/* ── Matched list + custom sections (all / discipline / geo) ── */}
                                    {audiences.some(a => a !== 'specific') && (
                                        <>
                                            {/* Empty state when no filter is selected yet */}
                                            {!audienceEnabled ? (
                                                <div className="py-8 text-center text-gray-400 text-[13px] border border-dashed border-gray-200">
                                                    {audiences.includes('discipline') && audiences.includes('geo')
                                                        ? 'Select roles above and choose a location (or leave Global) to see matching talent'
                                                        : audiences.includes('discipline')
                                                            ? 'Select one or more roles above to see matching talent'
                                                            : 'Select a region or enter a proximity location above to see matching talent'}
                                                </div>
                                            ) : (
                                                <>
                                                    {/* ── Section 1: Filter results (load-more accumulates here) ── */}
                                                    <SpinLoader isLoading={audienceLoading && audiencePage > 1}>
                                                        <RecipientList
                                                            matches={audienceRoster}
                                                            matchLabel={audienceMatchLabel}
                                                            excluded={excluded}
                                                            onToggleExclude={id => toggleSet(setExcluded, id)}
                                                            isLoading={audienceLoading && audiencePage === 1}
                                                            search={recipientSearch}
                                                            onSearchChange={setRecipientSearch}
                                                        />
                                                    </SpinLoader>
                                                    {hasMoreAudience && (
                                                        <button
                                                            onClick={() => setAudiencePage(p => p + 1)}
                                                            disabled={audienceLoading}
                                                            className={cx(btnSecondary, 'w-full justify-center mt-2')}
                                                        >
                                                            {audienceLoading ? 'Loading…' : `Load more (${audienceData.total - audienceRoster.length} remaining)`}
                                                        </button>
                                                    )}

                                                    {/* ── Section 2: Custom-added talent (separate card) ── */}
                                                    {customAddedOutside.length > 0 && (
                                                        <div className="mt-4 border border-emerald-200">
                                                            <div className="px-3 py-2 bg-emerald-50 border-b border-emerald-200">
                                                                <span className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">
                                                                    {customAddedOutside.length} added outside filter
                                                                </span>
                                                            </div>
                                                            {customAddedOutside.map((t, idx) => (
                                                                <div
                                                                    key={t.id}
                                                                    className="flex items-center gap-3 py-2.5 px-3.5 border-b border-gray-100 last:border-b-0 bg-emerald-50/30"
                                                                >
                                                                    <Checkbox checked={true}/>
                                                                    <UserImageCircle user={t} size={32}/>
                                                                    <div className="flex-1 min-w-0">
                                                                        <div className="text-[13px] font-medium truncate">{t.name}</div>
                                                                        <div className="text-[11.5px] text-gray-400">
                                                                            {t.disc}{t.loc ? ` · ${t.loc}` : ''}
                                                                        </div>
                                                                    </div>
                                                                    <button
                                                                        onClick={() => handleToggleCustomAdd(t)}
                                                                        className="shrink-0 text-[11px] font-semibold text-gray-400 hover:text-red-600 bg-transparent border-none cursor-pointer transition-colors"
                                                                    >
                                                                        Remove ×
                                                                    </button>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* ── Summary bar ── */}
                                                    <div className="mt-2 flex items-center gap-3 px-3 py-2.5 border border-gray-200 bg-gray-50 text-[12px]">
                                                        <span className="flex-1 text-gray-600">
                                                            {/* Contextual left-side message:
                                                                - Count request in flight:    "Updating recipient count…" (never show a stale total)
                                                                - Active name search + loaded: "Showing X results of Y users searching 'term'" + count
                                                                - No name search:              just the sending count */}
                                                            {audienceCountLoading ? (
                                                                <span className="inline-flex items-center gap-2 text-gray-400 italic">
                                                                    <Spin size="small"/> Updating recipient count…
                                                                </span>
                                                            ) : (
                                                                <>
                                                                    {debouncedRecipientSearch && (
                                                                        <span>
                                                                            Showing <strong>{audienceData?.users?.length ?? 0}</strong> of <strong>{audienceData?.total ?? stableAudienceTotal}</strong> talent searching &ldquo;<em>{debouncedRecipientSearch}</em>&rdquo; &mdash;&nbsp;
                                                                        </span>
                                                                    )}
                                                                    Sending to <strong className="text-indigo-600">{sendingCount}</strong> talent
                                                                </>
                                                            )}
                                                        </span>
                                                        {excluded.size > 0 && (
                                                            <span className="font-medium text-red-500">{excluded.size} excluded</span>
                                                        )}
                                                        {customAddedOutside.length > 0 && (
                                                            <span className="font-medium text-emerald-600">{customAddedOutside.length} added outside filter</span>
                                                        )}
                                                    </div>

                                                    {/* ── Section 3: Add talent outside the filter (not available for role-based categories or Announcement+All Talent) ── */}
                                                    {!CLIENT_CATEGORIES.includes(formModel.category) && !(formModel.category === 1 && audiences.includes('all')) && (
                                                        <div className="mt-4">
                                                            <AddTalentPanel
                                                                matches={audienceRoster}
                                                                customAdded={customAddedSet}
                                                                onToggleCustomAdd={handleToggleCustomAdd}
                                                            />
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}
                                </>
                            )}

                            {/* Step 4 — Review & Send */}
                            {step === 4 && (
                                <>
                                    <h3 className="text-[15px] font-bold mb-1">Review & Send</h3>
                                    <p className="text-[13px] text-gray-500 mb-5">
                                        Confirm your message and recipients
                                    </p>
                                    <div className="bg-gray-100 p-4 mb-5">
                                        <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">Message Preview</div>
                                        <p className="font-xs mb-2">
                                            Below is a preview of how the message will look to the talent
                                        </p>
                                        <TalentMessageDetailView
                                            msg={previewMessage}
                                            isRead={true}
                                        />
                                    </div>
                                    <div className="bg-gradient-to-br from-indigo-50 to-indigo-100/60 border border-indigo-200 p-5">
                                        <div className="flex items-baseline gap-2 mb-1">
                                            <span className="text-4xl font-bold text-indigo-600 tracking-tight">{recipientCfg.n}</span>
                                            <span className="text-[13px] text-gray-500">estimated recipients</span>
                                        </div>
                                        <p className="text-[13px] text-gray-500 mb-3.5">From matching {recipientCfg.summary}</p>
                                        {recipientCfg.samples.length > 0 && (
                                            <>
                                                <div className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-2">Sample recipients</div>
                                                <div className="flex flex-col gap-1.5">
                                                    {recipientCfg.samples.map((user, i) => (
                                                        <div key={user.id} className="flex items-center gap-2 text-xs text-gray-500">
                                                            <UserImageCircle user={user} size={32}/> {user.name}
                                                        </div>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between py-4 px-6 bg-white border-t border-gray-200 -mt-4">
                            {step > 1
                                ? <button onClick={() => setStep(s => s - 1)} className={btnSecondary}>← Back</button>
                                : <div/>
                            }
                            <div className="flex gap-2">
                                {(() => {
                                    // Block advancing while the recipient list is still resolving —
                                    // the user hasn't finalized their audience until the count settles
                                    const waitingOnAudience = step === 3 && audienceCountLoading
                                    return (
                                        <button
                                            onClick={() => step === 4 ? setShowModal(true) : setStep(s => s + 1)}
                                            disabled={waitingOnAudience}
                                            title={waitingOnAudience ? 'Waiting for the recipient list to finish loading' : undefined}
                                            className={cx(btnPrimary, 'disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-indigo-600')}
                                        >
                                            {waitingOnAudience
                                                ? 'Loading recipients…'
                                                : step === 4 ? 'Send Now ✓' : 'Continue →'}
                                        </button>
                                    )
                                })()}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Send confirmation modal */}
            {showModal && (
                <div
                    onClick={() => !sendMessageMutation.isLoading && handleCloseModal()}
                    className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center backdrop-blur-sm"
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        className="bg-white p-7 max-w-[420px] w-[90%] shadow-2xl text-center"
                    >
                        {/* Loading */}
                        {sendMessageMutation.isLoading && (
                            <>
                                <Spin size="large"/>
                                <p className="mt-4 text-sm text-gray-500">Sending your message…</p>
                            </>
                        )}

                        {/* Error */}
                        {sendMessageMutation.isError && (
                            <>
                                <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-4 text-red-500">
                                    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                        <line x1="18" y1="6" x2="6" y2="18"/>
                                        <line x1="6" y1="6" x2="18" y2="18"/>
                                    </svg>
                                </div>
                                <h3 className="text-lg font-bold mb-1.5">Send Failed</h3>
                                <p className="text-sm text-gray-500 leading-relaxed mb-5">
                                    {sendMessageMutation.error?.message || 'Something went wrong. Please try again.'}
                                </p>
                                <div className="flex gap-2.5 justify-center">
                                    <button onClick={handleCloseModal} className={btnSecondary}>Cancel</button>
                                    <button onClick={() => sendMessageMutation.mutate()} className={btnPrimary}>Try Again</button>
                                </div>
                            </>
                        )}

                        {/* Success */}
                        {sendMessageMutation.isSuccess && (
                            <>
                                <div className="w-14 h-14 bg-emerald-50 rounded-full flex items-center justify-center mx-auto mb-4 text-emerald-500">
                                    <svg width="24" height="24" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                        <polyline points="20,6 9,17 4,12"/>
                                    </svg>
                                </div>
                                <h3 className="text-lg font-bold mb-1.5">Message Sent!</h3>
                                <p className="text-sm text-gray-500 leading-relaxed mb-5">
                                    Your message has been delivered to <strong>{recipientCfg.n} recipients</strong>.
                                </p>
                                <div className="flex gap-2.5 justify-center">
                                    <button onClick={handleCloseModal} className={btnSecondary}>Close</button>
                                    <button onClick={handleSend} className={btnPrimary}>View Sent Log</button>
                                </div>
                            </>
                        )}

                        {/* Confirmation (idle — not yet sent) */}
                        {!sendMessageMutation.isLoading && !sendMessageMutation.isError && !sendMessageMutation.isSuccess && (
                            <>
                                <h3 className="text-lg font-bold mb-1.5">Send Message?</h3>
                                <p className="text-sm text-gray-500 leading-relaxed mb-5">
                                    You're about to send this message to <strong>{recipientCfg.n} recipient{recipientCfg.n !== 1 ? 's' : ''}</strong>. This cannot be undone.
                                </p>
                                <div className="flex gap-2.5 justify-center">
                                    <button onClick={handleCloseModal} className={btnSecondary}>Cancel</button>
                                    <button onClick={() => sendMessageMutation.mutate()} className={btnPrimary}>Confirm & Send</button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {/* Discipline change confirmation modal */}
            {pendingDiscChange !== null && (
                <div
                    onClick={() => setPendingDiscChange(null)}
                    className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center backdrop-blur-sm"
                >
                    <div
                        onClick={e => e.stopPropagation()}
                        className="bg-white p-7 max-w-[420px] w-[90%] shadow-2xl"
                    >
                        <div className="w-12 h-12 bg-amber-50 rounded-full flex items-center justify-center mb-4 text-amber-500">
                            <svg width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                                <line x1="12" y1="9" x2="12" y2="13"/>
                                <line x1="12" y1="17" x2="12.01" y2="17"/>
                            </svg>
                        </div>
                        <h3 className="text-[15px] font-bold mb-1.5">Reset exclusions?</h3>
                        <p className="text-[13px] text-gray-500 leading-relaxed mb-5">
                            Changing the selected roles will reset the <strong>{excluded.size} talent</strong> you've excluded from the current filter. Any talent you've added outside the filter will be kept.
                        </p>
                        <div className="flex gap-2.5">
                            <button
                                onClick={() => setPendingDiscChange(null)}
                                className={cx(btnSecondary, 'flex-1 justify-center')}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => {
                                    applyDiscChange(pendingDiscChange)
                                    setPendingDiscChange(null)
                                }}
                                className={cx(btnPrimary, 'flex-1 justify-center bg-amber-500 hover:bg-amber-600')}
                            >
                                Yes, change roles
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

export default AdminMessageNew